import React, { useEffect, useState } from 'react'
import {
  Button,
  Modal,
  Pagination,
  Tabs,
  Dropdown,
  Menu,
  Tag,
  Tooltip,
  Card,
  Popconfirm,
  Input,
  DatePicker,
  Row,
  Col,
  Form,
  Select,
  message,
  Typography,
} from 'antd'
import { useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { RootState, useAppDispatch, useAppSelector } from '~/Store/hooks'
import { UserActions } from '~/Store/User'
import {
  ListLisencesIdDelete,
  UserService as MainUserService,
} from '~/Services'
import { AdminUserService } from '~/Services/AdminUsers'
import { OrganizationsService } from '~/Services/OrganizationService'
import dayjs from 'dayjs'
import weekday from 'dayjs/plugin/weekday'
import localeData from 'dayjs/plugin/localeData'
import utc from 'dayjs/plugin/utc'
import { AddCompanyModal } from './AddCompanyModal'
import { DeleteCompanyModal } from './DeleteCompanyModal'
import {
  DeleteOutlined,
  EditOutlined,
  CaretDownOutlined,
  InfoCircleOutlined,
  GiftOutlined,
  MoreOutlined,
  PlusCircleOutlined,
  CloseCircleOutlined,
  SaveOutlined,
} from '@ant-design/icons'
import './AdminOrganizations.css'
import Title from 'antd/lib/skeleton/Title'
import { getLicenseTypes, LicenseMgmtActions } from '~/Store/License'
import { stat } from 'fs'

dayjs.extend(weekday)
dayjs.extend(localeData)
dayjs.locale('en')
dayjs.locale('vi')
dayjs.extend(utc)

export const AdminOrganizations = () => {
  const { t } = useTranslation()
  const [isAddCompanyModalVisible, setIsAddCompanyModalVisible] =
    useState(false)
  const [isDeleteCompanyModalVisible, setIsDeleteCompanyModalVisible] =
    useState(false)
  const [selectedOrganization, setSelectedOrganization] = useState<any>(null)
  const [isEditingDetail, setIsEditingDetail] = useState(false)
  const [editForm] = Form.useForm()

  const dispatch = useAppDispatch()
  const dataorga = useSelector(
    (state: RootState) => state.user.listOrganizationData,
  )
  const lisencesData = useSelector((state: RootState) => state.user.lisenceData)
  const licenseTypes = useAppSelector(getLicenseTypes)

  const [isModalVisible, setIsModalVisible] = useState(false)
  const [currentRecord, setCurrentRecord] = useState<any>(null)
  const [currentPage, setCurrentPage] = useState(1)
  const pageSize = 10

  const [usersList, setUsersList] = useState<string[]>([])
  const [isUsersModalVisible, setIsUsersModalVisible] = useState(false)
  const [currentLicense, setCurrentLicense] = useState<any>(null)
  const [newLicense, setNewLicense] = useState('')
  const [orgID, setOrgID] = useState('')
  const [isAddingLicense, setIsAddingLicense] = useState(false)
  const [usersData, setUsersData] = useState<any>(null)
  const [inputValue, setInputValue] = useState(0)

  if (typeof ResizeObserver !== 'undefined') {
    const resizeObserver = new ResizeObserver(() => {})
    resizeObserver.observe(document.body)
  }
  const [isLicenseExists, setIsLicenseExists] = useState(false)

  const handleLicenseChange = value => {
    const licenseDetails = lisencesData?.map(license => ({
      guid: license.licenseType.guid,
      name: license.licenseType.name,
    }))
    const isLicenseExists = licenseDetails.some(
      license => license.guid === value,
    )
    if (isLicenseExists) {
      message.error(t('admin.licenseExists'))
    }
    setIsLicenseExists(isLicenseExists)
  }

  const fetchListOrganization = async () => {
    try {
      dispatch(UserActions.getListOrganizationDataRequest())
      const req: any = await MainUserService.Get.getListOrganization()
      const { data } = req
      dispatch(UserActions.setListOrganizationDataSuccess(data))
    } catch (err) {
      console.error('Error fetching organization list:', err)
      dispatch(UserActions.setListOrganizationDataFailure(err))
    }
  }

  useEffect(() => {
    fetchListOrganization()
  }, [dispatch])

  const dataSource =
    dataorga?.results.map((org: any) => ({
      key: org.guid,
      companyName: org.companyName,
      companyId: org.companyId,
      code: org.code,
      taxCode: org.taxCode,
      note: org.note,
      billingAddress: org.billingAddress,
      billingAddress2: org.billingAddress2,
      billingCity: org.billingCity,
      billingState: org.billingState,
      billingZip: org.billingZip,
      crmId: org.crmId,
      duns: org.duns,
      naics: org.naics,
      phoneNumber: org.phoneNumber,
      userOrganization: org.userOrganization,
      licenses: org.licenses,
      organizationRoles: org.organizationRoles,
      status: org.status,
    })) || []

  const paginatedData = dataSource.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize,
  )

  const handleModalOk = () => {
    setIsModalVisible(false)
  }

  const handleModalCancel = () => {
    setIsModalVisible(false)
    setCurrentRecord(null)
    setIsEditingDetail(false)
    editForm.resetFields()
  }

  const handleEdit = async (record: any) => {
    try {
      dispatch(UserActions.searchUserRequest())
      const reqUsers: any = await MainUserService.Get.getUsersByOrganization(
        record.key,
      )
      const { data: usersData } = reqUsers
      dispatch(UserActions.searchUserSuccess(usersData))
      setUsersData(usersData?.results)
      setOrgID(record.key)
      dispatch(UserActions.getLisencesRequest())
      const req: any = await MainUserService.Get.getLicensesById(record.key)
      const { data } = req

      dispatch(UserActions.setLisencesSuccess(data))

      const formattedLicenses = data.map((license: any) => ({
        ...license,
        licenseName: license.licenseType?.name,
        users: license.users.map((user: any) => ({
          name: user.email,
          id: user.id,
        })),
      }))

      setCurrentRecord({
        ...record,
        licenses: formattedLicenses,
      })
    } catch (err) {
      console.error('Error fetching licenses:', err)
    }

    setIsModalVisible(true)
  }

  const handleDeleteClick = (record: any, e: React.MouseEvent) => {
    e.stopPropagation()
    setSelectedOrganization(record)
    setIsDeleteCompanyModalVisible(true)
  }

  const handleDeleteSuccess = () => {
    fetchListOrganization()
    setSelectedOrganization(null)
  }

  const handleDelete = async (user: any) => {
    try {
      dispatch(UserActions.deleteRequest())
      const input = [user.id]
      const req: any = await MainUserService.Delete.deleteUser(user.id, input)

      if (req === 1) {
        const updatedLicenses = currentRecord?.licenses?.filter(
          (license: any) => license.users.every((u: any) => u.id !== user.id),
        )
        setCurrentRecord({
          ...currentRecord,
          licenses: updatedLicenses,
        })
      } else if (req === -1) {
        console.error('Error deleting user: Operation failed')
      } else {
        console.error('Unexpected API response:', req)
      }
    } catch (err) {
      console.error('Error deleting user:', err)
    }
  }

  const showRecordDetails = async (record: any) => {
    try {
      dispatch(UserActions.searchUserRequest())
      const reqUsers: any = await MainUserService.Get.getUsersByOrganization(
        record.key,
      )
      const { data: usersData } = reqUsers
      dispatch(UserActions.searchUserSuccess(usersData))
      setUsersData(usersData?.results)

      dispatch(UserActions.getLisencesRequest())
      const req: any = await MainUserService.Get.getLicensesById(record.key)
      setOrgID(record.key)
      const { data } = req

      dispatch(UserActions.setLisencesSuccess(data))
      const formattedLicenses = data.map((license: any) => ({
        ...license,
        licenseName: license.licenseType?.name,
        users: license.users.map((user: any) => ({
          name: user.email,
          id: user.id,
        })),
      }))

      setCurrentRecord({
        ...record,
        licenses: formattedLicenses,
      })
    } catch (err) {
      console.error('Error fetching licenses:', err)
    }
    setIsModalVisible(true)
  }

  const showUsersList = (users: string[], license: any) => {
    setInputValue(license.number)
    setUsersList(users)
    setCurrentLicense(license)
    setIsUsersModalVisible(true)
  }

  const handleUsersModalCancel = () => {
    setIsUsersModalVisible(false)
  }

  const handleAddLicense = () => {
    setIsAddingLicense(true)
  }

  const handleSaveLicense = () => {
    if (newLicense.trim()) {
      setIsAddingLicense(false)
      setNewLicense('')
    } else {
      message.error(t('admin.pleaseEnterLicenseName'))
    }
  }

  const formatDate = (date: string) => {
    return dayjs(date).format('DD/MM/YYYY')
  }

  const handleDateChange = (date: any) => {
    if (currentLicense) {
      setCurrentLicense({
        ...currentLicense,
        expiredate: date ? date.local().format('YYYY-MM-DD') : null,
      })
    }
  }

  const handleCloseAddLicense = () => {
    setIsAddingLicense(false)
    setNewLicense('')
  }

  const handleUpdateExpireDate = async () => {
    if (!currentLicense) {
      console.error('No license data available for update')
      return
    }
    try {
      const updatedExpireDate = currentLicense?.expiredate
      const activeDate = currentLicense.activeDate || dayjs().toISOString()
      const licenseTypeId =
        currentLicense.licenseTypeId || 'default-license-type-id'
      const limitDevices = currentLicense.limitDevices || 0
      const number = inputValue || 0

      const input = [
        {
          expiredate: updatedExpireDate,
          licenseTypeId: licenseTypeId,
          activeDate: activeDate,
          limitDevices: limitDevices,
          number: number,
        },
      ]
      const orgId = currentLicense.organizationId
      const req: any = await MainUserService.Put.updateDateLicense(orgId, input)
      dispatch(UserActions.getLisencesRequest())

      setIsModalVisible(false)
    } catch (err) {
      console.error('Error updating license expiration date:', err)
    }
    setIsUsersModalVisible(false)
  }

  const getInvitationStatusColor = (status: string) => {
    switch (status) {
      case 'Accepted':
        return 'green'
      case 'Pending':
        return 'red'
      case 'Declined':
        return 'blue'
      default:
        return 'black'
    }
  }

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'Member':
        return '#28a745'
      case 'Admin':
        return '#dc3545'
      case 'SuperUser':
        return '#007bff'
      case 'Leader':
        return '#ffc107'
      default:
        return '#6c757d'
    }
  }

  const verifyNullStatus = (status: string | null | undefined): string => {
    if (status === null || status === undefined) {
      return t('admin.none')
    }
    return status
  }

  const handleInputChange = e => {
    const value = e.target.value
    setInputValue(Number(value))
  }

  const [newLicenseModalVisible, setNewLicenseModalVisible] = useState(false)

  const addNewLicense = () => {
    dispatch(
      LicenseMgmtActions.getLicenseTypes({
        paging: true,
        pageSize: 1000,
        page: 1,
      }),
    )
    setNewLicenseModalVisible(true)
  }

  const cancelNewLicense = () => {
    setNewLicenseModalVisible(false)
  }

  const handleNewLicenseOk = async (record: any) => {
    if (!record || typeof record !== 'object') {
      console.error('record must be an object')
      return
    }

    const input = [
      {
        expiredate: record.endDate.toISOString(),
        licenseTypeId: record.licenseName,
        activeDate: record.startDate.toISOString(),
        limitDevices: parseInt(record.userCount),
        number: parseInt(record.userCount),
      },
    ]

    try {
      dispatch(UserActions.createNewLisencesRequest())
      const req: any = await MainUserService.Post.createNewLicense(orgID, input)
      message.success(t('admin.addLicenseSuccess'))
      
      // Refresh licenses
      const reqLicenses: any = await MainUserService.Get.getLicensesById(orgID)
      const { data } = reqLicenses
      dispatch(UserActions.setLisencesSuccess(data))
      
      const formattedLicenses = data.map((license: any) => ({
        ...license,
        licenseName: license.licenseType?.name,
        users: license.users.map((user: any) => ({
          name: user.email,
          id: user.id,
        })),
      }))

      setCurrentRecord({
        ...currentRecord,
        licenses: formattedLicenses,
      })
    } catch (err) {
      console.error('Error creating license:', err)
      message.error(t('admin.addLicenseFailed'))
    }
    setNewLicenseModalVisible(false)
  }

  const handleEditDetail = () => {
    editForm.setFieldsValue({
      companyName: currentRecord?.companyName,
      code: currentRecord?.code,
      taxCode: currentRecord?.taxCode,
      note: currentRecord?.note,
      duns: currentRecord?.duns,
      phoneNumber: currentRecord?.phoneNumber,
      billingAddress: currentRecord?.billingAddress,
      billingAddress2: currentRecord?.billingAddress2,
      billingCity: currentRecord?.billingCity,
      billingState: currentRecord?.billingState,
      billingZip: currentRecord?.billingZip,
      naics: currentRecord?.naics,
    })
    setIsEditingDetail(true)
  }

  const handleCancelEdit = () => {
    setIsEditingDetail(false)
    editForm.resetFields()
  }

  const handleSaveEdit = async () => {
    try {
      const values = await editForm.validateFields()

      const params = {
        companyName: values.companyName,
        code: values.code,
        taxCode: values.taxCode,
        note: values.note,
        billingAddress: values.billingAddress,
        billingAddress2: values.billingAddress2,
        billingCity: values.billingCity,
        billingState: values.billingState,
        billingZip: values.billingZip,
        duns: values.duns,
        phoneNumber: values.phoneNumber,
        naics: values.naics,
        guid: currentRecord.key,
      }

      await OrganizationsService.Put.updateOrganizationInfo(
        currentRecord.key,
        params,
      )

      message.success(t('admin.updateOrgSuccess'))

      setCurrentRecord({
        ...currentRecord,
        ...params,
      })

      await fetchListOrganization()

      setIsEditingDetail(false)
      editForm.resetFields()
    } catch (err) {
      console.error('Error updating organization:', err)
      message.error(t('admin.updateFailed'))
    }
  }

  return (
    <div className="AdminOrganizations-container">
      <div className="table-container">
        <Row align="stretch">
          <Typography.Title style={{ flex: 1 }} level={4}>
            {t('admin.organizations')}
          </Typography.Title>
          <Button
            type="primary"
            icon={<PlusCircleOutlined />}
            onClick={() => setIsAddCompanyModalVisible(true)}
          >
            {t('admin.addCompany')}
          </Button>
        </Row>
        {paginatedData.map(item => (
          <div
            key={item.key}
            className="table-row"
            onClick={() => showRecordDetails(item)}
          >
            <div className="table-cell">{item.companyName || ''}</div>
            <div className="table-cell">{item.code || '-'}</div>
            <div className="table-cell">{item.taxCode || '-'}</div>
            <div className="table-cell">{item.billingAddress || ''}</div>
            <div className="table-cell">{item.duns || ''}</div>
            <div className="table-cell">{item.naics || ''}</div>
            <div className="table-cell">{item.organizationRoles || ''}</div>
            <div className="table-cell status">
              <span className={`status ${item.status}`}>
                {verifyNullStatus(item.status)}
              </span>
            </div>
            <div className="table-cell actions">
              <Button
                type="link"
                icon={<EditOutlined />}
                onClick={e => {
                  e.stopPropagation()
                  handleEdit(item)
                }}
              />
              <Button
                type="link"
                icon={<DeleteOutlined />}
                danger
                onClick={e => handleDeleteClick(item, e)}
              />
            </div>
          </div>
        ))}
      </div>
      <Pagination
        style={{ textAlign: 'center' }}
        current={currentPage}
        pageSize={pageSize}
        total={dataSource.length}
        onChange={page => setCurrentPage(page)}
      />

      <Modal
        visible={isModalVisible}
        className="custom-modal"
        footer={null}
        maskClosable={true}
        onCancel={handleModalCancel}
        width={800}
      >
        <Tabs defaultActiveKey="1">
          <Tabs.TabPane tab={t('admin.detailInfo')} key="1">
            {!isEditingDetail ? (
              <div style={{ padding: '20px' }}>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '20px',
                  }}
                >
                  <Typography.Title level={4} style={{ margin: 0 }}>
                    {t('admin.detailInfo')}
                  </Typography.Title>
                  <Button
                    type="primary"
                    icon={<EditOutlined />}
                    onClick={handleEditDetail}
                  >
                    {t('admin.editOrganization')}
                  </Button>
                </div>

                <Row gutter={[24, 16]}>
                  <Col span={12}>
                    <div style={{ marginBottom: '16px' }}>
                      <div
                        style={{
                          color: '#8c8c8c',
                          fontSize: '12px',
                          marginBottom: '4px',
                        }}
                      >
                        {t('admin.companyName')}
                      </div>
                      <div style={{ fontSize: '14px', color: '#262626' }}>
                        {currentRecord?.companyName || '-'}
                      </div>
                    </div>
                  </Col>

                  <Col span={12}>
                    <div style={{ marginBottom: '16px' }}>
                      <div
                        style={{
                          color: '#8c8c8c',
                          fontSize: '12px',
                          marginBottom: '4px',
                        }}
                      >
                        {t('admin.code')}
                      </div>
                      <div style={{ fontSize: '14px', color: '#262626' }}>
                        {currentRecord?.code || '-'}
                      </div>
                    </div>
                  </Col>

                  <Col span={12}>
                    <div style={{ marginBottom: '16px' }}>
                      <div
                        style={{
                          color: '#8c8c8c',
                          fontSize: '12px',
                          marginBottom: '4px',
                        }}
                      >
                        {t('admin.taxCode')}
                      </div>
                      <div style={{ fontSize: '14px', color: '#262626' }}>
                        {currentRecord?.taxCode || '-'}
                      </div>
                    </div>
                  </Col>

                  <Col span={12}>
                    <div style={{ marginBottom: '16px' }}>
                      <div
                        style={{
                          color: '#8c8c8c',
                          fontSize: '12px',
                          marginBottom: '4px',
                        }}
                      >
                        {t('admin.duns')}
                      </div>
                      <div style={{ fontSize: '14px', color: '#262626' }}>
                        {currentRecord?.duns || '-'}
                      </div>
                    </div>
                  </Col>

                  <Col span={12}>
                    <div style={{ marginBottom: '16px' }}>
                      <div
                        style={{
                          color: '#8c8c8c',
                          fontSize: '12px',
                          marginBottom: '4px',
                        }}
                      >
                        {t('admin.phone')}
                      </div>
                      <div style={{ fontSize: '14px', color: '#262626' }}>
                        {currentRecord?.phoneNumber || '-'}
                      </div>
                    </div>
                  </Col>

                  <Col span={12}>
                    <div style={{ marginBottom: '16px' }}>
                      <div
                        style={{
                          color: '#8c8c8c',
                          fontSize: '12px',
                          marginBottom: '4px',
                        }}
                      >
                        {t('admin.naics')}
                      </div>
                      <div style={{ fontSize: '14px', color: '#262626' }}>
                        {currentRecord?.naics || '-'}
                      </div>
                    </div>
                  </Col>

                  <Col span={12}>
                    <div style={{ marginBottom: '16px' }}>
                      <div
                        style={{
                          color: '#8c8c8c',
                          fontSize: '12px',
                          marginBottom: '4px',
                        }}
                      >
                        {t('admin.homeAddress')}
                      </div>
                      <div style={{ fontSize: '14px', color: '#262626' }}>
                        {currentRecord?.billingAddress || '-'}
                      </div>
                    </div>
                  </Col>

                  <Col span={12}>
                    <div style={{ marginBottom: '16px' }}>
                      <div
                        style={{
                          color: '#8c8c8c',
                          fontSize: '12px',
                          marginBottom: '4px',
                        }}
                      >
                        {t('admin.companyAddress')}
                      </div>
                      <div style={{ fontSize: '14px', color: '#262626' }}>
                        {currentRecord?.billingAddress2 || '-'}
                      </div>
                    </div>
                  </Col>

                  <Col span={8}>
                    <div style={{ marginBottom: '16px' }}>
                      <div
                        style={{
                          color: '#8c8c8c',
                          fontSize: '12px',
                          marginBottom: '4px',
                        }}
                      >
                        {t('admin.city')}
                      </div>
                      <div style={{ fontSize: '14px', color: '#262626' }}>
                        {currentRecord?.billingCity || '-'}
                      </div>
                    </div>
                  </Col>

                  <Col span={8}>
                    <div style={{ marginBottom: '16px' }}>
                      <div
                        style={{
                          color: '#8c8c8c',
                          fontSize: '12px',
                          marginBottom: '4px',
                        }}
                      >
                        {t('admin.state')}
                      </div>
                      <div style={{ fontSize: '14px', color: '#262626' }}>
                        {currentRecord?.billingState || '-'}
                      </div>
                    </div>
                  </Col>

                  <Col span={8}>
                    <div style={{ marginBottom: '16px' }}>
                      <div
                        style={{
                          color: '#8c8c8c',
                          fontSize: '12px',
                          marginBottom: '4px',
                        }}
                      >
                        {t('admin.zip')}
                      </div>
                      <div style={{ fontSize: '14px', color: '#262626' }}>
                        {currentRecord?.billingZip || '-'}
                      </div>
                    </div>
                  </Col>

                  <Col span={24}>
                    <div style={{ marginBottom: '16px' }}>
                      <div
                        style={{
                          color: '#8c8c8c',
                          fontSize: '12px',
                          marginBottom: '4px',
                        }}
                      >
                        {t('admin.note')}
                      </div>
                      <div style={{ fontSize: '14px', color: '#262626' }}>
                        {currentRecord?.note || '-'}
                      </div>
                    </div>
                  </Col>
                </Row>
              </div>
            ) : (
              <Form
                form={editForm}
                layout="vertical"
                onFinish={handleSaveEdit}
                style={{ padding: '20px' }}
              >
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '20px',
                  }}
                >
                  <Typography.Title level={4} style={{ margin: 0 }}>
                    {t('admin.organizationInformation')}
                  </Typography.Title>
                  <div style={{ display: 'flex', gap: '10px' }}>
                    <Button danger onClick={handleCancelEdit}>
                      {t('admin.cancel')}
                    </Button>
                    <Button
                      type="primary"
                      htmlType="submit"
                      icon={<SaveOutlined />}
                    >
                      {t('admin.saveChanges')}
                    </Button>
                  </div>
                </div>

                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item
                      label={t('admin.companyName')}
                      name="companyName"
                      rules={[
                        {
                          required: true,
                          message: t('admin.pleaseEnterCompanyName'),
                        },
                      ]}
                    >
                      <Input />
                    </Form.Item>
                  </Col>

                  <Col span={12}>
                    <Form.Item
                      label={t('admin.code')}
                      name="code"
                      rules={[{ max: 50, message: t('admin.codeMaxLength') }]}
                    >
                      <Input />
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item
                      label={t('admin.taxCode')}
                      name="taxCode"
                      rules={[{ max: 50, message: t('admin.taxCodeMaxLength') }]}
                    >
                      <Input />
                    </Form.Item>
                  </Col>

                  <Col span={12}>
                    <Form.Item
                      label={t('admin.duns')}
                      name="duns"
                    >
                      <Input />
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item label={t('admin.phone')} name="phoneNumber">
                      <Input />
                    </Form.Item>
                  </Col>

                  <Col span={12}>
                    <Form.Item label={t('admin.naics')} name="naics">
                      <Input />
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item
                      label={t('admin.homeAddress')}
                      name="billingAddress"
                    >
                      <Input />
                    </Form.Item>
                  </Col>

                  <Col span={12}>
                    <Form.Item
                      label={t('admin.companyAddress')}
                      name="billingAddress2"
                    >
                      <Input />
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={16}>
                  <Col span={8}>
                    <Form.Item
                      label={t('admin.city')}
                      name="billingCity"
                    >
                      <Input />
                    </Form.Item>
                  </Col>

                  <Col span={8}>
                    <Form.Item label={t('admin.state')} name="billingState">
                      <Input />
                    </Form.Item>
                  </Col>

                  <Col span={8}>
                    <Form.Item label={t('admin.zipCode')} name="billingZip">
                      <Input />
                    </Form.Item>
                  </Col>
                </Row>

                <Form.Item label={t('admin.note')} name="note">
                  <Input.TextArea rows={3} />
                </Form.Item>
              </Form>
            )}
          </Tabs.TabPane>

          <Tabs.TabPane tab={t('users')} key="2">
            {usersData ? (
              <div className="table-container">
                {usersData.map((user: any) => (
                  <div key={user.id}>
                    <div className="table-row">
                      <div className="table-cell">{`${user.lastname} ${user.firstname}`}</div>
                      <div className="table-cell">{user.email}</div>
                      <div
                        className="table-cell"
                        style={{
                          display: 'flex',
                          gap: '8px',
                          flexWrap: 'wrap',
                        }}
                      >
                        {user.organizationRoles.map((role: string) => (
                          <div key={role} style={{ color: getRoleColor(role) }}>
                            {role}
                          </div>
                        ))}
                      </div>
                      <div
                        style={{
                          color: getInvitationStatusColor(
                            user.invitationStatus,
                          ),
                        }}
                      >
                        {user.invitationStatus || ''}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p>{t('admin.loadingUsers')}</p>
            )}
          </Tabs.TabPane>

          <Tabs.TabPane tab={t('licenses')} key="3">
            <div
              className="licenses-tab-content"
              style={{ padding: '0 20px 20px 20px' }}
            >
              <p
                style={{
                  fontSize: '16px',
                  fontWeight: 'bold',
                  marginBottom: '15px',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                {t('admin.licenseList')}:
                <PlusCircleOutlined
                  style={{ marginRight: '8px', cursor: 'pointer' }}
                  onClick={addNewLicense}
                />
              </p>
              {Array.isArray(currentRecord?.licenses) &&
              currentRecord?.licenses.length > 0 ? (
                <div
                  className="licenses-list"
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
                    gap: '15px',
                  }}
                >
                  {currentRecord?.licenses.map(
                    (license: any, index: number) => (
                      <Tag
                        key={index}
                        className="license-tag"
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          padding: '10px 15px',
                          borderRadius: '8px',
                          boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
                          backgroundColor: '#fafafa',
                          border: '1px solid #d9d9d9',
                        }}
                      >
                        <GiftOutlined
                          style={{
                            fontSize: '35px',
                            color: 'red',
                            marginRight: '15px',
                          }}
                        />
                        <div
                          style={{
                            flex: 1,
                            overflow: 'hidden',
                            whiteSpace: 'nowrap',
                            textOverflow: 'ellipsis',
                          }}
                        >
                          <span
                            style={{
                              fontSize: '14px',
                              fontWeight: 'bold',
                              color: '#333',
                            }}
                          >
                            {license?.licenseName} ({license?.number}{' '}
                            {t('users')})
                          </span>
                        </div>
                        <Tooltip title={t('admin.licenseInfo')}>
                          <InfoCircleOutlined
                            style={{
                              cursor: 'pointer',
                              color: 'red',
                              fontSize: '18px',
                              marginLeft: '10px',
                            }}
                            onClick={() =>
                              showUsersList(license.users, license)
                            }
                          />
                        </Tooltip>
                      </Tag>
                    ),
                  )}
                </div>
              ) : (
                <div
                  style={{
                    textAlign: 'center',
                    padding: '20px',
                    backgroundColor: '#f9f9f9',
                    borderRadius: '8px',
                    border: '1px dashed #d9d9d9',
                    fontSize: '14px',
                    color: '#888',
                  }}
                >
                  <span>{t('admin.noLicenses')}</span>
                </div>
              )}
              <Modal
                title={t('admin.addNewLicense')}
                visible={newLicenseModalVisible}
                onCancel={cancelNewLicense}
                footer={null}
              >
                <Form layout="vertical" onFinish={handleNewLicenseOk}>
                  <Form.Item
                    label={t('admin.licenseName')}
                    name="licenseName"
                    rules={[
                      {
                        required: true,
                        message: t('admin.pleaseSelectLicense'),
                      },
                    ]}
                  >
                    <Select
                      placeholder={t('admin.selectLicense')}
                      onChange={handleLicenseChange}
                    >
                      {licenseTypes.map(license => (
                        <Select.Option key={license.guid} value={license.guid}>
                          {license.name}
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>

                  <Form.Item
                    label={t('admin.userCount')}
                    name="userCount"
                    rules={[
                      {
                        required: true,
                        message: t('admin.pleaseEnterUserCount'),
                      },
                    ]}
                  >
                    <Input type="number" />
                  </Form.Item>

                  <Form.Item
                    label={t('admin.startDate')}
                    name="startDate"
                    rules={[
                      {
                        required: true,
                        message: t('admin.pleaseSelectStartDate'),
                      },
                    ]}
                  >
                    <DatePicker style={{ width: '100%' }} />
                  </Form.Item>

                  <Form.Item
                    label={t('admin.endDate')}
                    name="endDate"
                    rules={[
                      {
                        required: true,
                        message: t('admin.pleaseSelectEndDate'),
                      },
                    ]}
                  >
                    <DatePicker style={{ width: '100%' }} />
                  </Form.Item>

                  <Form.Item>
                    <Button
                      type="primary"
                      htmlType="submit"
                      disabled={isLicenseExists}
                    >
                      {t('admin.addLicense')}
                    </Button>
                  </Form.Item>
                </Form>
              </Modal>
            </div>
          </Tabs.TabPane>
        </Tabs>
      </Modal>

      <Modal
        title={t('admin.userList')}
        visible={isUsersModalVisible}
        onCancel={handleUsersModalCancel}
        footer={null}
        centered
        bodyStyle={{ padding: '20px' }}
      >
        <div
          style={{
            fontWeight: 'bold',
            color: '#333',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            fontSize: '16px',
            marginBottom: '15px',
          }}
        >
          <p>
            {t('admin.licenseName')}: {currentLicense?.licenseName}{' '}
            <input
              type="number"
              value={inputValue}
              onChange={handleInputChange}
              style={{
                marginRight: '5px',
                width: '50px',
                border: '1px solid #EEEEEE',
                fontSize: '14px',
                textAlign: 'center',
              }}
            />
            {t('users')}
          </p>
        </div>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            gap: '20px',
            marginBottom: '20px',
          }}
        >
          <div style={{ flex: 1 }}>
            <strong
              style={{ color: '#555', fontSize: '14px', display: 'block' }}
            >
              {t('admin.activeDate')}:
            </strong>
            <DatePicker
              style={{
                width: '100%',
                marginTop: '5px',
                borderColor: '#d9d9d9',
                backgroundColor: '#f5f5f5',
                cursor: 'not-allowed',
                borderRadius: '5px',
              }}
              value={
                currentLicense?.activeDate
                  ? dayjs(currentLicense.activeDate).utc()
                  : null
              }
              disabled
              format="DD/MM/YYYY"
              placeholder={t('admin.activeDate')}
            />
          </div>
          <div style={{ flex: 1 }}>
            <strong
              style={{ color: '#555', fontSize: '14px', display: 'block' }}
            >
              {t('admin.expireDate')}:
            </strong>
            <DatePicker
              style={{
                width: '100%',
                marginTop: '5px',
                borderColor: '#4CAF50',
                backgroundColor: '#fff',
                borderRadius: '5px',
              }}
              value={
                currentLicense?.expiredate
                  ? dayjs(currentLicense.expiredate).utc()
                  : null
              }
              onChange={handleDateChange}
              format="DD/MM/YYYY"
              placeholder={t('admin.expireDate')}
            />
          </div>
        </div>

        {isAddingLicense && (
          <div
            style={{
              position: 'absolute',
              top: '20%',
              left: '50%',
              transform: 'translateX(-50%)',
              width: '300px',
              backgroundColor: '#fff',
              padding: '20px',
              borderRadius: '8px',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
              zIndex: 1000,
            }}
          >
            <Button
              icon={<CloseCircleOutlined />}
              onClick={handleCloseAddLicense}
              style={{
                position: 'absolute',
                top: '10px',
                right: '10px',
                border: 'none',
                background: 'transparent',
                fontSize: '18px',
                color: '#f00',
              }}
            />
            <h3 style={{ textAlign: 'center', marginBottom: '15px' }}>
              {t('admin.addNewUser')}
            </h3>
            <Input
              value={newLicense}
              onChange={e => setNewLicense(e.target.value)}
              placeholder={t('admin.pleaseEnterUsername')}
              style={{
                width: '100%',
                marginBottom: '15px',
                borderRadius: '5px',
                padding: '8px',
              }}
            />
            <Button
              type="primary"
              onClick={handleSaveLicense}
              style={{ width: '100%' }}
            >
              {t('admin.add')}
            </Button>
          </div>
        )}

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '10px',
            marginBottom: '20px',
            maxHeight: '300px',
            overflowY: 'auto',
          }}
        >
          {usersList.map((user, index) => (
            <div
              key={index}
              style={{
                padding: '10px',
                borderRadius: '8px',
                border: '1px solid #d9d9d9',
                backgroundColor: '#f9f9f9',
                color: '#333',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              {user.name}
            </div>
          ))}
        </div>

        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <Button
            type="primary"
            onClick={handleUpdateExpireDate}
            style={{ width: '150px' }}
          >
            {t('admin.save')}
          </Button>
        </div>
      </Modal>

      <AddCompanyModal
        visible={isAddCompanyModalVisible}
        onClose={() => setIsAddCompanyModalVisible(false)}
        onSuccess={fetchListOrganization}
      />

      <DeleteCompanyModal
        visible={isDeleteCompanyModalVisible}
        organizationId={selectedOrganization?.key || ''}
        organizationName={selectedOrganization?.companyName || ''}
        onClose={() => {
          setIsDeleteCompanyModalVisible(false)
          setSelectedOrganization(null)
        }}
        onSuccess={handleDeleteSuccess}
        deleteService={AdminUserService.Delete.deleteOrganization}
      />
    </div>
  )
}

export default AdminOrganizations